// Phase-A R1.2 — route-id → Persian breadcrumb label. Static dictionary.
// Phase-B will layer dynamic context-aware labels (e.g., resolved course
// title for `/course/:courseId`) via a future resolver hook; R1.2 keeps
// the surface minimal — just a lookup and a fallback to the raw id/param.

export const BREADCRUMB_LABELS: Record<string, string> = {
  home: "خانه", programs: "برنامه‌ها", catalog: "کاتالوگ", "my-courses": "دوره‌های من",
  progress: "پیشرفت", tutor: "دستیار AI", classroom: "کلاس زنده", dashboard: "میز کار",
  course: "درس", "course-live": "کلاس زنده درس", instructor: "کنسول استاد",
  admissions: "پذیرش", credential: "گواهی‌ها", search: "جستجو", assessment: "آزمون",
  "assessment-live": "آزمون زنده", community: "انجمن", analytics: "تحلیل",
  authoring: "استودیو", recordings: "بایگانی کلاس‌ها", audit: "گزارش حسابرسی",
  login: "ورود", register: "ثبت‌نام", forgot: "بازیابی رمز", "verify-email": "تأیید ایمیل",
  "2fa-setup": "تأیید دومرحله‌ای", onboarding: "آغاز کار", settings: "تنظیمات",
  // S-2 nav fix — labels for the /settings/:tab deep-link segments so the
  // crumb reads «تنظیمات › امنیت» instead of a raw «security».
  security: "امنیت", privacy: "حریم خصوصی",
  calendar: "تقویم", library: "کتابخانه", help: "پشتیبانی", pricing: "پلن‌ها",
  faculty: "هیات علمی", admin: "میز مدیریت", parent: "میز والد", officehours: "Office Hours",
  events: "رویدادها", about: "درباره ما", schools: "دانشکده‌ها", labs: "آزمایشگاه‌ها",
  virtuallab: "آزمایشگاه مجازی", research: "پژوهش", inbox: "صندوق ورودی",
  messages: "پیام‌ها", bookmarks: "ذخیره‌ها", achievements: "دستاوردها",
  submission: "تمرین‌ها", profile: "پروفایل", transcript: "شناسنامهٔ آموزشی",
  "degree-audit": "مسیر شایستگی/رتبه", registration: "نامزدسازی سازمانی", career: "مسیر ارتقای شغلی من",
  "financial-aid": "کمک‌هزینه", wellness: "سلامت", alumni: "فارغ‌التحصیلان",
  hackathons: "هکاتون", "honor-code": "صداقت علمی",
  // R-SIS routes (انتخاب واحد، چارت، پیشخوان خدمت + آستانه‌ها/گردش‌کار/نیم‌سال در زیرشاخهٔ مدیریت)
  "add-drop": "حذف و اضافه", "curriculum-builder": "چارت درسی",
  "service-desk": "پیشخوان خدمت", new: "درخواست جدید",
  terms: "نیم‌سال‌ها", "degree-policies": "آستانه‌های مقطع", workflows: "گردش‌کارها",
  // R-PAY routes
  tuition: "شهریه و پرداخت", "tuition-config": "پیکربندی شهریه",
  // R-MARKET P1 — «بازار دانش» authoring studio; P5 — economics surfaces;
  // P7 — in-browser recording studio + Q&A/payout (recording label below).
  market: "بازار پکیج آموزشی", studio: "استودیوی دانش", earnings: "درآمد من",
  // S-NRI-ADAPT — «سازندهٔ پکیج» studio home.
  "creator-studio": "استودیو دانش",
  record: "استودیوی ضبط", wishlist: "علاقه‌مندی‌های من",
  // S-21 — labels for the routes that grew AFTER R1.2 (the nav-coherence
  // vitest asserts every sidebar-reachable segment has one). Second-segment
  // words (hr, finance, …) label the `param` crumb of two-segment admin routes.
  wallet: "کیف پول", agents: "دستیارهای هوشمند", checkout: "پرداخت اشتراک",
  "my-grades": "نمرات من", "my-attendance": "حضور من", "my-path": "مسیر تحصیلی",
  assignments: "تکالیف", exam: "اتاق آزمون", "assessment-builder": "آزمون‌ساز",
  "question-bank": "بانک سؤال", "item-analysis": "تحلیل سؤال",
  "grading-queue": "صف تصحیح", "proctor-review": "بازبینی ناظر",
  "live-class": "کلاس زنده", "grade-submission": "گردش رسمی نمره",
  roster: "دانشجویان", attendance: "حضورغیاب", "class-analytics": "تحلیل کلاس",
  "instructor-dashboard": "میز استاد", ta: "میز دستیار آموزشی",
  "faculty-dashboard": "میز دانشکده", commission: "کمیسیون موارد خاص",
  hr: "استخدام", regulation: "آیین‌نامه آموزشی", finance: "مالی", withdrawals: "برداشت‌ها", people: "افراد",
  org: "میز سازمان", moderate: "میز ناظر", support: "میز پشتیبانی",
  counselor: "میز مشاور", more: "بیشتر",
  // S-21 — the remaining sidebar-reachable segments (surfaced by the
  // nav-coherence vitest sweep; mostly second segments of /admin/* routes).
  "adaptive-path": "مسیر تطبیقی", "ai-governance": "حاکمیت AI",
  "analytics-admin": "تحلیل‌های مدیریتی", applications: "درخواست‌های پذیرش",
  "auto-rules": "قواعد خودکار", broadcast: "اطلاع‌رسانی همگانی",
  "bulk-enroll": "ثبت‌نام گروهی", claims: "حق‌التدریس", clearance: "تسویه‌حساب",
  "cognitive-profile": "نیم‌رخ شناختی", cohorts: "گروه‌های ثبت‌نام",
  "community-moderation": "پایش انجمن", content: "محتوا",
  "content-analytics": "تحلیل محتوا", "content-review": "بازبینی محتوا",
  copilot: "همیار هوشمند", "counseling-appointments": "نوبت‌های مشاوره",
  "counselor-caseload": "پرونده‌های مشاوره", "counselor-inbox": "صندوق مشاور",
  "course-content": "محتوای درس", courses: "دروس", "crisis-queue": "صف بحران",
  decrees: "احکام کارگزینی", departments: "گروه‌های آموزشی",
  enrollments: "مدیریت ثبت‌نام", faculties: "دانشکده‌ها",
  "faculty-reports": "گزارش‌های دانشکده", "flagged-reports": "گزارش‌های نشان‌شده",
  gradebook: "دفتر نمره", grades: "نمرات", "instructor-assignment": "تخصیص استاد",
  instructors: "اساتید", integrations: "یکپارچه‌سازی‌ها", invoices: "صورتحساب‌ها",
  loans: "وام‌ها", "ministry-library": "کتابخانهٔ وزارت", "moderation-log": "لاگ پایش",
  monitoring: "پایش سامانه", "my-chart": "چارت درسی من",
  "my-payslips": "فیش‌های حقوقی من", network: "شبکهٔ آکادمیک",
  "notif-templates": "قالب‌های اعلان", offerings: "ارائهٔ دروس",
  // S-NRI-2 — /admin/organizations (درخت سازمانی وزارت→واحد). Required by the
  // nav-coherence vitest: every sidebar-reachable segment needs a label here.
  organizations: "ساختار سازمانی",
  // S-NRI-3.5 — homes for the 7 organizational/oversight roles.
  "ministry-dashboard": "میز ناظر وزارت", "holding-dashboard": "میز مدیر هلدینگ",
  "company-dashboard": "میز ادمین شرکت", "training-dashboard": "میز مدیر آموزش",
  "audit-dashboard": "میز حسابرس", "certification-desk": "میز صدور گواهی",
  "assessor-dashboard": "میز ارزیاب عملی",
  "parent-links": "پیوند والدین", payroll: "حقوق‌ودستمزد",
  "payroll-config": "پیکربندی حقوق", plans: "پلن‌ها", portfolio: "کارپوشه",
  "probation-review": "بازبینی مشروطی", profiles: "پروفایل‌ها",
  "reference-data": "داده‌های پایه", refund: "استرداد", reports: "گزارش‌ها",
  review: "بازبینی", roles: "نقش‌ها", "rubric-builder": "روبریک‌ساز",
  schedule: "برنامهٔ هفتگی", seats: "صندلی‌ها", specializations: "گرایش‌ها",
  "stage-progress": "پیشرفت مرحله‌ای", "student-overview": "پروندهٔ افراد",
  students: "دانشجویان", super: "ابزار راهبری", tenants: "سازمان‌ها",
  tickets: "تیکت‌ها", treasury: "خزانه", "user-lookup": "جستجوی کاربر",
  users: "کاربران",
};

export const breadcrumbLabel = (id: string): string => BREADCRUMB_LABELS[id] || id;
